import { toast } from "../../toast/toast";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { getAlertsCount } from "../../fetch/AlertRequest";
import { borrowKey, getReagentKeyStatus } from "../../fetch/KeyRequest";
import { getReagentInfo, updateReagentQuantity } from "../../fetch/logRequest";
import { errorMessage } from "../../function/errorMessage";
import keyStyles from "../../styles/modules/KeyPage.module.css";
import styles from "../../styles/modules/infoTab.module.css";
import "../../styles/sanitize.css";
import {
  Reagent,
  ReagentGroup,
  ReagentKeyStatus,
  ReagentLog,
  ReagentSizeDisp,
  Unit,
  User,
} from "../../../../backend/src/types";

interface InfoTabProps {
  reagent: Reagent;
  units: Unit[];
  setReagent: React.Dispatch<React.SetStateAction<Reagent | null>>;
  onLoading: React.Dispatch<React.SetStateAction<boolean>>;
  reloadReagent: (id: string) => Promise<void>;
}

export interface GetReagentInfoRes {
  reagentGroup: ReagentGroup;
  totalQuantity: number;
  displaySizes: ReagentSizeDisp[];
  reagentLabAdminList: User[];
}

const InfoTab: React.FC<InfoTabProps> = ({
  onLoading,
  units,
  reagent,
  setReagent,
  reloadReagent,
}) => {
  const { user, userLabs, setAlertCount } = useUser();
  const navigate = useNavigate();

  const [targetUnit, setTargetUnit] = useState<Unit | null>(null);
  const [changeUnit, setChagneUnit] = useState<Unit | null>(null);

  const [reagentGroup, setReagentGroup] = useState<ReagentGroup | null>(null);
  const [reagentSizeList, setReagentSizeList] = useState<ReagentSizeDisp[]>([]);

  const [quantity, setQuantity] = useState<number>(0);
  const [amount, setAmount] = useState<number | null>(null);

  const [action, setAction] = useState<string>("use");

  // 試薬庫の鍵の状態。鍵管理ONの試薬庫では、鍵を借りている人だけが使用・返却できる。
  const [keyStatus, setKeyStatus] = useState<ReagentKeyStatus | null>(null);
  const isKeyLocked = keyStatus?.key_managed === true && !keyStatus.held_by_me;

  useEffect(() => {
    if (user) loadReagent(reagent);
  }, [user, reagent]);

  async function loadReagent(reagent: Reagent) {
    onLoading(true);

    await loadReagentInfo(reagent.group_id);
    await loadUnit(reagent, units);
    await loadKeyStatus(reagent.id);

    onLoading(false);
  }

  async function loadReagentInfo(group_id: number) {
    const result = await getReagentInfo({ group_id });
    if (result) {
      setReagentGroup(result.reagentGroup);
      setQuantity(result.totalQuantity);
      setReagentSizeList(result.displaySizes);
    }
  }

  async function loadKeyStatus(reagent_id: number) {
    setKeyStatus(await getReagentKeyStatus(reagent_id));
  }

  // 試薬画面から直接その試薬庫の鍵を借りる。
  async function handleBorrowKey() {
    if (!keyStatus?.storage_location_id) return;
    onLoading(true);
    try {
      await borrowKey(keyStatus.storage_location_id);
      toast.success("鍵を借りました。");
      await loadKeyStatus(reagent.id);
    } catch (error) {
      errorMessage("鍵の貸出に失敗しました。", error);
      await loadKeyStatus(reagent.id);
    } finally {
      onLoading(false);
    }
  }

  async function loadUnit(reagent: Reagent, units: Unit[]) {
    let findChangeUnit: Unit | undefined;
    switch (reagent.display_unit) {
      case "g":
      case "mg":
        findChangeUnit = units.find((unit) => unit.code === "mg");
        break;
      case "L":
      case "mL":
        findChangeUnit = units.find((unit) => unit.code === "mL");
        break;
      case "ea":
        findChangeUnit = units.find((unit) => unit.code === "ea");
        break;
    }

    if (findChangeUnit) {
      setChagneUnit(findChangeUnit);
    }

    const findTargetUnit = units.find(
      (unit) => unit.code === reagent.display_unit,
    );

    if (findTargetUnit) {
      setTargetUnit(findTargetUnit);
    }
  }

  async function handleUpdateReagentQuantity() {
    if (!reagent) return toast.error("対象の試薬が見つかりません。");
    if (isKeyLocked) {
      return toast.error(
        keyStatus?.holder_name
          ? `この試薬庫の鍵は${keyStatus.holder_name}さんが貸出中です。`
          : "この試薬庫の鍵を借りてから操作してください。"
      );
    }
    if (!amount) return toast.error("数量が入力されていません。");
    if (!validateChange(reagent)) return;
    if (!targetUnit || !changeUnit) return;

    let quantity_used: number | null = null;
    const quantity_in = null;

    if (action === "use") {
      quantity_used = amount * changeUnit.to_base_factor;
    } else if (action === "return") {
      quantity_used = -amount * changeUnit.to_base_factor;
    } else {
      toast.error("使用または返却のいずれかを選択してください。");
      return;
    }

    const capacity_base = reagent.capacity_base * targetUnit.to_base_factor;
    const quantity_base = reagent.quantity_base * targetUnit.to_base_factor;

    const afterChangeQuantity = quantity_base - quantity_used;

    if (afterChangeQuantity < 0) {
      toast.error("使用量が残量を超えているため、変更できません。");
      return;
    }

    if (afterChangeQuantity > capacity_base) {
      toast.error("返却量が容量を超えているため、変更できません。");
      return;
    }

    const sendReagentLog: ReagentLog = {
      id: 0,
      reagent_id: reagent.id,
      tenant_id: reagent.tenant_id,
      lab_id: reagent.lab_id,
      user_id: user?.id ?? 0,
      manager_id: null,
      action,
      quantity_used,
      quantity_in,
      quantity_remaining: afterChangeQuantity,
      remarks: "",
    };

    onLoading(true);

    const res = await updateReagentQuantity({ sendReagentLog });
    if (res === 409) {
      reloadReagent(String(reagent.id));
      return;
    }
    await handleUseReagentAlert();

    const newQuantity_base = afterChangeQuantity / targetUnit.to_base_factor;

    setReagent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        quantity_base: newQuantity_base,
      };
    });

    resetInput();
    onLoading(false);
  }

  function resetInput() {
    setAmount(0);
  }

  function validateChange(reagent: Reagent): boolean {
    if (reagent.status === "disposed") {
      toast.error("廃棄された試薬は使用・返却ができません。");
      return false;
    }

    if (amount === null || isNaN(amount) || amount <= 0) {
      toast.error("使用量には0より大きい値を入力してください。");
      return false;
    }

    if (!Number.isInteger(amount)) {
      toast.error("入力に小数点を含めないでください");
      return false;
    }

    return true;
  }

  async function handleUseReagentAlert() {
    if (user?.tenant_id) {
      const tenant_id = user?.tenant_id;
      const lab_ids = userLabs.map((lab) => lab.id);
      const alertCount = await getAlertsCount({ tenant_id, lab_ids });
      setAlertCount(alertCount);
    }
  }

  return (
    <>
      <div className={styles["info-tab-container"]}>
        {reagentGroup && (
          <table className={styles["table"]}>
            <tbody>
              <tr>
                <th>試薬マスター</th>
                <td>{reagentGroup.name}</td>
              </tr>
              {reagentSizeList.length > 0 && (
                <tr>
                  <th>在庫数</th>
                  <td>
                    {reagentSizeList.map((size) => (
                      <div
                        key={size.id}
                        style={{
                          display: "inline-block",
                          marginRight: "0.7rem",
                        }}
                      >
                        {size.capacity_base}
                        {size.unit}（{size.stock}）
                      </div>
                    ))}
                  </td>
                </tr>
              )}
              <tr>
                <th>総残量</th>
                <td>
                  {quantity}
                  {reagentGroup.total_unit}
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {reagent && (
          <table className={styles["table"]}>
            <tbody>
              <tr>
                <th>試薬名</th>
                <td>{reagent.name}</td>
              </tr>
              <tr>
                <th>試薬ID</th>
                <td>{reagent.id}</td>
              </tr>
              <tr>
                <th>残量</th>
                <td>
                  {reagent.quantity_base}/{reagent.capacity_base}
                  {reagent.display_unit}
                </td>
              </tr>
              <tr>
                <th>保管場所</th>
                <td>{reagent.storage_location_name || "-"}</td>
              </tr>
              <tr>
                <th>備考</th>
                <td>{reagent.location_code || "-"}</td>
              </tr>
              {keyStatus?.key_managed && (
                <tr>
                  <th>鍵</th>
                  <td>
                    <span
                      className={`${keyStyles["badge"]} ${
                        keyStatus.held_by_me ? keyStyles["lend"] : keyStyles["keep"]
                      }`}
                    >
                      {keyStatus.held_by_me ? "貸出中" : "保管中"}
                    </span>
                    {keyStatus.held_by_me
                      ? "　あなたがこの試薬庫の鍵を借りています"
                      : keyStatus.holder_name
                      ? `　${keyStatus.holder_name}さんが貸出中です`
                      : "　あなたはこの試薬庫の鍵を借りていません"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className={styles["reagent-use-container"]}>
        <div className={styles["reagent-use"]}>
          {isKeyLocked && (
            <div className={keyStyles["lock-msg"]}>
              <span>
                {keyStatus?.holder_name
                  ? `この試薬庫の鍵は${keyStatus.holder_name}さんが貸出中のため使用できません。`
                  : "この試薬庫は鍵管理の対象です。鍵を借りてから使用してください。"}
              </span>
              {!keyStatus?.holder_name && (
                <button type="button" onClick={handleBorrowKey}>
                  鍵を借りる
                </button>
              )}
            </div>
          )}
          <section>
            <label>
              <input
                type="radio"
                name="action"
                value="use"
                checked={action === "use"}
                disabled={isKeyLocked}
                onChange={(e) => setAction(e.target.value)}
              />
              使用
            </label>
            <label>
              <input
                type="radio"
                name="action"
                value="return"
                checked={action === "return"}
                disabled={isKeyLocked}
                onChange={(e) => setAction(e.target.value)}
              />
              返却
            </label>
            {(() => {
              const actionHelpText: Record<string, string> = {
                use: "使用した試薬の量を記入してください。記入した値だけ試薬が減算されます。誤って減らしすぎた場合、適切な値になるように返却モードで加算してください。",
                return:
                  "返却した試薬の量を記入してください。記入した値だけ試薬が加算されます。誤って増やしすぎた場合、適切な値になるように使用モードで減算してください。",
              };
              return actionHelpText[action] ? (
                <p>{actionHelpText[action]}</p>
              ) : null;
            })()}
          </section>
          <section>
            <label>
              {action === "use" ? "使用量" : "返却量"}
              <input
                type="number"
                inputMode="numeric"
                placeholder={action === "use" ? "使用量を入力" : "返却量を入力"}
                id="amount-input"
                name="amount-input"
                disabled={isKeyLocked}
                value={amount ?? ""}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setAmount(value === 0 ? null : value);
                }}
              />
              {changeUnit?.code}
            </label>
          </section>
          <button
            type="button"
            className={action === "use" ? styles["use"] : styles["return"]}
            disabled={isKeyLocked}
            onClick={() => handleUpdateReagentQuantity()}
          >
            {action === "use" ? "使用" : "返却"}
          </button>
        </div>
      </div>
    </>
  );
};

export default InfoTab;
